import React, {useContext, useEffect, useMemo, useState} from 'react';
import {Grid} from '../../dimijs/src/lib/components/lists/Grid';
import {Pagination} from '../../dimijs/src/lib/components/lists/Pagination';

import {ICrudStatus, ISource} from '../../dimijs/src/lib/sources/source';
import { useLocationSearch } from '../../dimijs/src/lib/utils/locationSearch';
import { Button } from '../../dimijs/src/lib/components/Button';
import { Modal } from '../../dimijs/src/lib/components/Modal';
import { JsonForm } from '../../ui/JsonForm';
import {DataCtx} from '../../dimijs/src/lib/utils/dataContext';
import {IUser, useUserSource} from '../../sources/useUserSource';
import {IGuestUuid, getGuestsUuidSource} from '../../sources/guestsUuidSource';
import gridStyles from '../../dimijs/src/lib/components/lists/Grid.module.scss';

const newUser:IUser = {
  login: '',
  pass: '',
  role: '',
}

export const Users = () => {
  const {set, params} = useLocationSearch();
  const [selectedUser, setSelectedUser] = useState<IUser>({
    ...newUser,
  });
  const [formOpened, setFormOpened] = useState(false);
  const {store} = useContext(DataCtx);
  const usersSource = useUserSource();
  const guestsUuidSource = getGuestsUuidSource(store);
  const pagination = useMemo(() => {
    return {
      defaultCountPage: 10,
      queryParamCountOnPage: 'countUsersOnPage',
      queryParamPage: 'pageUsers',
    };
  }, []);
  
  const uuidPagination = useMemo(() => {
    return {
      defaultCountPage: 10,
      queryParamCountOnPage: 'countUuidOnPage',
      queryParamPage: 'pageUuid',
    };
  }, []);

  const uuidColumns = useMemo(() => {
    return [
      {name: 'uuid'},
      {name: 'surveyCount'},
      {name: 'firstSeen', Template: (props: {
        item: IGuestUuid;
        renderRowIndex: number;
      }) => {
        return (
          <div className={props.renderRowIndex % 2 ? gridStyles.odd_row : ''}>
            {new Date(props.item.firstSeen).toLocaleDateString()}
          </div>
        );
      },},
      {name: 'lastSeen', Template: (props: {
        item: IGuestUuid;
        renderRowIndex: number;
      }) => {
        return (
          <div className={props.renderRowIndex % 2 ? gridStyles.odd_row : ''}>
            {new Date(props.item.lastSeen).toLocaleDateString()}
          </div>
        );
      },},
      {name: 'delete', Template: (props: {
        item: IGuestUuid;
        source: ISource<IGuestUuid, string>;
        renderRowIndex: number;
      }) => {
        return (
          <div className={props.renderRowIndex % 2 ? gridStyles.odd_row : ''}>
            <Button caption="Delete" onClick={async ()=>{
              if (window.confirm(`Are you sure you want to delete all data for UUID "${props.item.uuid}"? This will delete ${props.item.surveyCount} survey results and related journey entries.`)) {
                try {
                  const response = await fetch('/api/users/delete_by_uuid', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ uuid: props.item.uuid }),
                    credentials: 'include'
                  });
                  
                  if (response.ok) {
                    // Refresh both grids
                    props.source.refresh();
                    usersSource.refresh();
                  } else {
                    const error = await response.json();
                    alert(`Error deleting UUID data: ${error.error || error.message}`);
                  }
                } catch (error: any) {
                  alert(`Error deleting UUID data: ${error?.message || error}`);
                }
              }
            }}/>
          </div>
        );
      },},
    ];
  }, [usersSource]);
  const columns = useMemo(() => {
    return [
      {name: '_id'},
      {name: 'login', Template: (props: {
        item: IUser;
        source: ISource<IUser, string>;
        renderRowIndex: number;
      }) => {
        return (
          <div className={props.renderRowIndex % 2 ? gridStyles.odd_row : ''}>
            <Button caption={props.item.login} onClick={()=>{
              set('selectedUser', props.item._id);
            }}/>
          </div>
        );
      },},
      {name: 'pass'},
      {name: 'role'},
      {name: 'app'},
      {name: 'updatedBy'},
      {name: 'updated'},
      {name: 'delete', Template: (props: {
        item: IUser;
        source: ISource<IUser, string>;
        renderRowIndex: number;
      }) => {
        return (
          <div className={props.renderRowIndex % 2 ? gridStyles.odd_row : ''}>
            <Button caption="Delete" onClick={async ()=>{
              if (window.confirm(`Are you sure you want to delete user "${props.item.login}"? This will delete all their data including journey and survey results.`)) {
                try {
                  const response = await fetch('/api/users/delete_by_user', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ user: props.item.login }),
                    credentials: 'include'
                  });
                  
                  if (response.ok) {
                    // Refresh the grid
                    props.source.refresh();
                  } else {
                    const error = await response.json();
                    alert(`Error deleting user: ${error.error || error.message}`);
                  }
                } catch (error: any) {
                  alert(`Error deleting user: ${error?.message || error}`);
                }
              }
            }}/>
          </div>
        );
      },},
    ];
  }, []);
  const editFields = useMemo(()=>([
    {name: 'login', type: 'string' as const},
    {name: 'role', type: 'string' as const},
  ]), []);
  const createFields = useMemo(()=>([
    {name: 'login', type: 'string' as const},
    {name: 'pass', type: 'string' as const},
    {name: 'role', type: 'string' as const},
    {name: 'app', type: 'string' as const},
  ]), []);
  useEffect(()=>{
    if (!params.selectedUser) {
      return;
    }
    usersSource.read(params.selectedUser).then(data=>{
      if ((data as ICrudStatus).errorCode) {
        setSelectedUser({...newUser});
        return;
      }
      if (data!==null) {
        setSelectedUser(data as IUser);
        setFormOpened(true);
      }
    });
  }, [params.selectedUser]);
  return (
    <div>
      <h1>Users</h1>
      <Button caption='Add new' onClick={()=>{
        set('selectedUser', undefined);
        setSelectedUser({...newUser});
        setFormOpened(true);
      }}/>
      <Grid
        source={usersSource}
        pagination={pagination}
        columns={columns}
        header
      />
      
      <h2 style={{marginTop: '40px'}}>Guest UUIDs Management</h2>
      <p>Manage anonymous user data by UUID. Delete UUID data to clean up guest survey results and journey entries.</p>
      <Grid
        source={guestsUuidSource}
        pagination={uuidPagination}
        columns={uuidColumns}
        header
      />
      
      <Modal width={600} isOpen={formOpened} onClose={()=>{
        setFormOpened(false);
        set('selectedUser', undefined);
      }}>
        <div>
          <JsonForm fields={selectedUser._id ? editFields : createFields} value={selectedUser}
            onChange={setSelectedUser}/>
          <Button
            caption="Save"
            onClick={() => {
              if (!selectedUser._id) {
                usersSource.create([selectedUser]);
              } else {
                usersSource.update(selectedUser);
              }
            }}
          />
      <Button caption='Cancel' onClick={()=>{
        setFormOpened(false);
        set('selectedUser', undefined);
      }}/>
        </div>
      </Modal>
    </div>
  );
};
